import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, X, LocateFixed, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

// Default center: Kuwait City
const KUWAIT_CENTER = { lat: 29.3759, lng: 47.9774 };

interface MapPickerProps {
  latitude?: number;
  longitude?: number;
  onChange: (lat: number | undefined, lng: number | undefined) => void;
}

export function MapPicker({ latitude, longitude, onChange }: MapPickerProps) {
  const { t } = useTranslation();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const center = latitude && longitude ? { lat: latitude, lng: longitude } : KUWAIT_CENTER;

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY || mapLoaded) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      setMapLoaded(true);
      initMap();
    };
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  // Initialize map once script is loaded
  useEffect(() => {
    if (mapLoaded && !mapInstanceRef.current && mapRef.current && window.google) {
      initMap();
    }
  }, [mapLoaded]);

  const initMap = () => {
    if (!window.google?.maps || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 14,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    mapInstanceRef.current = map;

    const marker = new window.google.maps.Marker({
      position: center,
      map,
      draggable: true,
    });
    markerRef.current = marker;

    marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      if (pos) {
        onChange(pos.lat(), pos.lng());
      }
    });

    // Click to place marker
    map.addListener("click", (e: any) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      marker.setPosition({ lat, lng });
      onChange(lat, lng);
    });
  };

  // Auto-detect user location via browser geolocation
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(16);
          markerRef.current.setPosition({ lat, lng });
        }
        onChange(lat, lng);
        setDetecting(false);
      },
      (error) => {
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleClear = () => {
    onChange(undefined, undefined);
    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setPosition(KUWAIT_CENTER);
      mapInstanceRef.current.setCenter(KUWAIT_CENTER);
    }
  };

  // No API key — fallback to manual input
  if (!GOOGLE_MAPS_KEY) {
    return (
      <div className="space-y-2">
        <Input
          type="text"
          placeholder={t("map.pasteMapsLink")}
          value={latitude && longitude ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : ""}
          onChange={(e) => {
            const match = e.target.value.match(/(-?\d+\.?\d*)/g);
            if (match && match.length >= 2) {
              onChange(parseFloat(match[0]), parseFloat(match[1]));
            }
          }}
          className="h-12"
        />
        <p className="text-xs text-muted-foreground">{t("map.noApiKey")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Detect location button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDetectLocation}
        disabled={detecting}
        className="w-full"
        data-testid="button-detect-location"
      >
        {detecting ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <LocateFixed className="h-4 w-4 me-2" />
        )}
        {t("map.detectLocation")}
      </Button>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full h-64 rounded-xl border border-border overflow-hidden"
        data-testid="map-picker"
      />

      {/* Location status */}
      <div className="flex items-center justify-between">
        {latitude && longitude ? (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("map.tapToSet")}</span>
        )}
        {latitude && longitude && (
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 me-1" />
            {t("map.clear")}
          </Button>
        )}
      </div>
    </div>
  );
}
